// src/components/GoodsReceiptPrintView.jsx (or equivalent component directory)
import React, { useEffect } from 'react';
import { Printer, X } from 'lucide-react';

const GoodsReceiptPrintView = ({ receiptData, currency, grandTotal, onClose }) => {
    useEffect(() => {
        // Automatically trigger browser print dialog on mount
        window.print();
    }, []);

    if (!receiptData) return null;

    return (
        <div className="fixed inset-0 bg-white z-50 p-8 overflow-y-auto text-black print:p-0">
            {/* Print Controls / Close Button (Hidden when printing) */}
            <div className="flex justify-between items-center mb-6 pb-4 border-b print:hidden">
                <button
                    onClick={() => window.print()}
                    className="bg-emerald-600 text-white px-4 py-2 rounded shadow font-semibold hover:bg-emerald-700 flex items-center gap-2"
                >
                    <Printer size={16} /> Print / Save as PDF
                </button>
                <button
                    onClick={onClose}
                    className="bg-gray-200 text-gray-800 px-4 py-2 rounded font-semibold hover:bg-gray-300 flex items-center gap-2"
                >
                    <X size={16} /> Close Preview
                </button>
            </div>

            {/* Printable Document Content */}
            <div className="max-w-3xl mx-auto bg-white p-6 border rounded-lg shadow-sm print:border-none print:shadow-none">
                <div className="flex justify-between items-start border-b pb-6 mb-6">
                    <div>
                        <h1 className="text-2xl font-bold uppercase tracking-wide text-gray-900">Goods Receipt Note</h1>
                        <p className="text-sm text-gray-600 mt-1">Vendor: <span className="font-bold text-black">{receiptData.vendorname}</span></p>
                        <p className="text-xs text-gray-500 mt-1">Vendor ID: {receiptData.vendor_id}</p>
                    </div>
                    <div className="text-right">
                        <p className="text-sm font-semibold">Store: {receiptData.storename || 'Main Store'}</p>
                        <p className="text-xs text-gray-500 mt-1">Currency: {currency || 'NGN'}</p>
                    </div>
                </div>

                {/* Items Received Table */}
                <div className="mb-8">
                    <h3 className="font-bold text-sm uppercase text-gray-700 mb-3">Received Items</h3>
                    <table className="w-full text-left border-collapse text-sm">
                        <thead>
                            <tr className="border-b-2 border-gray-300 bg-gray-100 print:bg-gray-200">
                                <th className="p-2 font-semibold">Product Name</th>
                                <th className="p-2 text-center font-semibold">Ordered</th>
                                <th className="p-2 text-center font-semibold">Received</th>
                                <th className="p-2 text-right font-semibold">Unit Cost</th>
                                <th className="p-2 text-right font-semibold">Total</th>
                            </tr>
                        </thead>
                        <tbody>
                            {receiptData.items_received?.map((item, index) => {
                                const cost = parseFloat(item.cost) || 0;
                                const qty = parseFloat(item.quantity_received) || 0;
                                const lineTotal = cost * qty;
                                return (
                                    <tr key={index} className="border-b border-gray-200">
                                        <td className="p-2 font-medium">{item.productname}</td>
                                        <td className="p-2 text-center">{item.quantity_ordered}</td>
                                        <td className="p-2 text-center font-semibold">{item.quantity_received}</td>
                                        <td className="p-2 text-right">{currency} {cost.toFixed(2)}</td>
                                        <td className="p-2 text-right font-mono">{currency} {lineTotal.toFixed(2)}</td>
                                    </tr>
                                );
                            })}
                        </tbody>
                    </table>
                </div>

                {/* Grand Total Summary */}
                {grandTotal !== undefined && (
                    <div className="flex justify-end border-t pt-4 mb-8">
                        <div className="text-right">
                            <p className="text-xs text-gray-500 uppercase font-bold">Grand Total</p>
                            <p className="text-xl font-black text-gray-900">
                                {currency} {grandTotal.toLocaleString(undefined, { minimumFractionDigits: 2 })}
                            </p>
                        </div>
                    </div>
                )}

                {/* Footer summary */}
                <div className="border-t pt-4 text-xs text-gray-500 flex justify-between">
                    <span>System Generated Goods Receipt Document</span>
                    <span>Verified & Approved</span>
                </div>
            </div>
        </div>
    );
};

export default GoodsReceiptPrintView;